/**
 * E-GTCC-VIEW-TEN1, tab 3 — "Provisioning (Neuer Tenant)".
 *
 * Reference: `GTCC_REFERENCE_2026-09-01.html:718-724` — the panel
 * "Provisioning — Neuer Tenant (Wizard)" with four numbered `.row`s.
 *
 * The wizard is the existing create form (`GtccCreateTenantForm.tsx`), framed
 * in the reference's panel. That form is the only create path the platform
 * has, and it already carries steps 1 and 3 of the reference: name, type and
 * package template, plus the first admin with the one-time invitation link.
 *
 * Steps 2 and 4 have no create-time endpoint. `POST /v1/admin/tenants` takes
 * `displayName`, `type`, `packagePreset` and `initialAdminEmail` and nothing
 * else — limits and entitlements are set afterwards on the tenant
 * (Entitlements card, Tenant 360°), and the datastore assignment belongs to
 * the data plane's assign plan (E-GTCC-VIEW-DPLANE1). Those rows therefore
 * say where the step happens instead of offering a control that would have to
 * invent a write. No new write path (Owner order: "keine neuen Schreibpfade").
 */
import { Stack } from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import type { ReactNode } from "react";

import { GtccCreateTenantForm } from "../GtccCreateTenantForm.js";
import {
  GRID_COLUMN_STYLE,
  GRID_MAIN_STYLE,
  TenMutedNote,
  TenPanel,
  TenRow,
  type TenTabProps,
} from "../components/TenPanels.js";

export function ProvisioningTab(props: TenTabProps): ReactNode {
  const { t } = useLocale();

  return (
    <div style={GRID_MAIN_STYLE}>
      <div style={GRID_COLUMN_STYLE}>
        <TenPanel testId="gtcc-ten-provisioning" title={t("admin.gtcc.ten.provisioning.panel")}>
          <Stack gap={0}>
            <TenRow
              testId="gtcc-ten-provisioning-step1"
              tone="info"
              label={t("admin.gtcc.ten.provisioning.step1")}
              value={t("admin.gtcc.ten.provisioning.step1_value")}
            >
              {/*
               * Schritt 1 und 3 der Referenz sind dasselbe Formular — die alte
               * Konsole legt Tenant und ersten Admin in einem Aufruf an. Es
               * haengt an Schritt 1 und wird in Schritt 3 nur benannt.
               */}
              <GtccCreateTenantForm
                onOpenTenant={(tenantId) => props.onOpenTenant(tenantId, "t360")}
              />
            </TenRow>

            <TenRow
              testId="gtcc-ten-provisioning-step2"
              tone="muted"
              label={t("admin.gtcc.ten.provisioning.step2")}
              value={t("admin.gtcc.ten.provisioning.step2_value")}
            >
              <TenMutedNote testId="gtcc-ten-provisioning-step2-note">
                {t("admin.gtcc.ten.provisioning.step2_note")}
              </TenMutedNote>
            </TenRow>

            <TenRow
              testId="gtcc-ten-provisioning-step3"
              tone="info"
              label={t("admin.gtcc.ten.provisioning.step3")}
              value={t("admin.gtcc.ten.provisioning.step3_value")}
            >
              <TenMutedNote testId="gtcc-ten-provisioning-step3-note">
                {t("admin.gtcc.ten.provisioning.step3_note")}
              </TenMutedNote>
            </TenRow>

            <TenRow
              testId="gtcc-ten-provisioning-step4"
              tone="muted"
              label={t("admin.gtcc.ten.provisioning.step4")}
              value={t("admin.gtcc.ten.provisioning.step4_value")}
            >
              <TenMutedNote testId="gtcc-ten-provisioning-step4-note">
                {t("admin.gtcc.ten.provisioning.step4_note")}
              </TenMutedNote>
            </TenRow>
          </Stack>
        </TenPanel>
      </div>

      <div style={GRID_COLUMN_STYLE}>
        <TenPanel
          testId="gtcc-ten-provisioning-aside"
          title={t("admin.gtcc.ten.provisioning.aside")}
        >
          <TenMutedNote testId="gtcc-ten-provisioning-aside-note">
            {t("admin.gtcc.ten.provisioning.aside_note")}
          </TenMutedNote>
        </TenPanel>
      </div>
    </div>
  );
}
